import React from 'react';

type HlsPlayerProps = {
  src: string;
  autoPlay?: boolean;
  muted?: boolean;
  controls?: boolean;
  style?: React.CSSProperties;
  className?: string;
  live?: boolean;
};

const HARDWARE_HLS_RE = /\/hardware\/\d+\/hls\//;

function withAccessTokenQuery(url: string, rawToken: string): string {
  if (!url || url.includes('access_token=')) return url;
  const enc = encodeURIComponent(rawToken);
  return `${url}${url.includes('?') ? '&' : '?'}access_token=${enc}`;
}

function formatHlsFatalError(data: any): string {
  const code = data?.response?.code as number | undefined;
  const type = String(data?.type || '');
  const details = String(data?.details || '');

  if (code === 401 || code === 403) {
    return 'Brak dostępu do strumienia (wygasły token?). Odśwież stronę i zaloguj się ponownie.';
  }
  if (code === 404) {
    return 'Brak pliku HLS (404) — sprawdź, czy na Pi działa ffmpeg i czy w bazie jest poprawne urządzenie / kanał.';
  }
  if (code === 502 || code === 504) {
    return 'Serwer nie łączy się z urządzeniem HLS (Pi). Sprawdź sieć / Tailscale na VPS.';
  }

  if (type === 'networkError') {
    if (details === 'manifestLoadError') {
      return code
        ? `Nie pobrano listy HLS (manifest), HTTP ${code}.`
        : 'Nie pobrano manifestu (.m3u8) — często CORS (inny adres strony niż dozwolony w API) albo sieć. Ustaw CORS_EXTRA_ORIGINS na backendzie lub otwórz app z dozwolonej domeny. Szczegóły: konsola F12 → [HlsPlayer].';
    }
    if (details === 'levelLoadError') {
      return `Błąd poziomu strumienia (HTTP ${code ?? '—'}).`;
    }
    if (details === 'fragLoadError') {
      return 'Nie pobrano segmentu wideo (.ts) — przerwany strumień albo brak autoryzacji na segmentach.';
    }
  }
  if (type === 'mediaError') {
    if (details === 'bufferStalledError') {
      return 'Zatrzymany bufor (live / sieć). Spróbuj odświeżyć panel kamery.';
    }
    if (details === 'bufferAppendError') {
      return 'Nie można dopisać segmentu do bufora (MSE). Często: zerwany strumień RTSP, złe GOP/kluczowe klatki w ffmpeg albo uszkodzony plik .ts — zrestartuj ffmpeg na Pi i odśwież panel.';
    }
    if (details === 'bufferSeekOverHole' || details === 'bufferNudgeOnStall') {
      return 'Luka w strumieniu HLS (playlista vs segmenty). Odśwież panel; na Pi sprawdź stabilność ffmpeg i czas segmentów.';
    }
    if (details === 'manifestIncompatibleCodecsError') {
      return 'Przeglądarka nie obsługuje kodeków z tego manifestu (wideo/dźwięk). Na Pi ustaw transcoding na H.264 + AAC (np. libx264 + aac).';
    }
    if (details === 'fragParsingError' || details === 'fragDecryptError') {
      return 'Błąd parsowania segmentu .ts (uszkodzone dane lub szyfrowanie). Sprawdź ffmpeg i czy strumień nie jest zaszyfrowany (HLS AES).';
    }
    if (details === 'internalException' || details === 'remuxError') {
      return 'Błąd wewnętrzny remuxu HLS. Odśwież stronę; jeśli wraca — zmień parametry ffmpeg (krótsze segmenty, H.264 baseline).';
    }
    return `Błąd odtwarzania (media: ${details || 'nieznany'}). Konsola F12 → [HlsPlayer]. Często pomaga inna przeglądarka albo H.264+AAC z transcodingu na Pi.`;
  }

  const hint = [type && `typ: ${type}`, details && `szczegół: ${details}`, code && `HTTP ${code}`].filter(Boolean).join(' · ');
  return hint
    ? `Błąd HLS (${hint}). Pełny log: konsola F12 → [HlsPlayer].`
    : 'Błąd odtwarzania HLS — zobacz konsolę F12 (filtr [HlsPlayer]).';
}

/**
 * Odtwarzacz HLS: proxy /api/hardware/…/hls/ wymaga JWT (query i/lub Authorization).
 * Nie używamy blob: dla transmisji live — hls.js odświeża playlistę i wtedy względne
 * segmenty rozwiązywane względem blob tracą token → 401 na .ts.
 */
const HlsPlayer: React.FC<HlsPlayerProps> = ({ src, autoPlay = true, muted = true, controls = false, style, className, live = true }) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const empty = !src?.trim();

  React.useEffect(() => {
    setError(null);
    const video = videoRef.current;
    if (!video) return;
    if (!src?.trim()) {
      video.removeAttribute('src');
      video.load();
      return;
    }

    const isHardwareProxyHls = HARDWARE_HLS_RE.test(src);
    // Live z ffmpeg (krótki sliding window) — w Chrome/Firefox natywne HLS bywa złe lub „martwe”; zawsze hls.js.
    const canUseNative =
      !live && !isHardwareProxyHls && Boolean(video.canPlayType('application/vnd.apple.mpegurl'));
    if (canUseNative) {
      video.src = src;
      video.play().catch(() => {});
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    let destroyed = false as boolean;
    let driftTimer: number | undefined;
    let stallHandler: (() => void) | undefined;
    let fatalMediaRecoveryAttempts = 0;
    const maxFatalMediaRecoveries = 4;
    let lastRecoverMediaErrorAt = 0;
    const recoverMediaThrottleMs = 5000;
    let hls: any = (window as any).Hls || null;

    const clearDrift = () => {
      if (driftTimer != null) {
        clearInterval(driftTimer);
        driftTimer = undefined;
      }
    };

    const attach = () => {
      if (!video) return;
      if (!hls || !hls.isSupported?.()) return setError('Brak wsparcia HLS (hls.js) w tej przeglądarce.');

      // ffmpeg na Pi: ~1 s segmenty. cache:'no-store' — bez tego przeglądarka potrafi trzymać stary .m3u8/.ts i „live” stoi.
      const instance = new hls({
        enableWorker: false,
        lowLatencyMode: false,
        liveDurationInfinity: true,
        liveSyncDuration: 4,
        liveMaxLatencyDuration: 30,
        maxLiveSyncPlaybackRate: 1.75,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        backBufferLength: 60,
        maxBufferHole: 0.5,
        startPosition: -1,
        fetchSetup: (context: { url?: string }, initParams: RequestInit) => {
          try {
            const token = localStorage.getItem('auth_token');
            let reqUrl = String(context?.url || '');
            const abs = /^https?:\/\//i.test(reqUrl) ? reqUrl : new URL(reqUrl, src).href;
            let finalUrl = abs;
            if (token && HARDWARE_HLS_RE.test(abs)) {
              finalUrl = withAccessTokenQuery(abs, token);
            }
            const headers = new Headers(initParams.headers ?? undefined);
            if (token && HARDWARE_HLS_RE.test(abs) && !headers.has('Authorization')) {
              headers.set('Authorization', `Bearer ${token}`);
            }
            return new Request(finalUrl, { ...initParams, headers, cache: 'no-store' });
          } catch {
            return new Request(context.url as string, { ...initParams, cache: 'no-store' });
          }
        },
        xhrSetup: (xhr: XMLHttpRequest, reqUrl: string) => {
          try {
            const token = localStorage.getItem('auth_token');
            if (!token) return;
            const abs = /^https?:\/\//i.test(reqUrl) ? reqUrl : new URL(reqUrl, src).href;
            if (!HARDWARE_HLS_RE.test(abs)) return;
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          } catch {
            /* ignore */
          }
        },
      });
      (video as any).__hls = instance;

      const tryRecoverMediaError = () => {
        const now = Date.now();
        if (now - lastRecoverMediaErrorAt < recoverMediaThrottleMs) return;
        lastRecoverMediaErrorAt = now;
        instance.recoverMediaError();
      };

      stallHandler = () => {
        if (destroyed) return;
        try {
          void video.play();
        } catch {}
      };
      video.addEventListener('stalled', stallHandler);
      video.addEventListener('waiting', stallHandler);

      instance.on?.(hls.Events.ERROR, (_evt: any, data: any) => {
        if (!data) return;
        if (!data.fatal) {
          try {
            if (data.type === 'networkError') {
              instance.startLoad();
              return;
            }
            if (data.type === 'mediaError') {
              tryRecoverMediaError();
              return;
            }
          } catch {}
          return;
        }
        // hls.js bywa oznacza mediaError jako fatal mimo że recoverMediaError() / swapAudioCodec() jeszcze ratuje odtwarzanie.
        if (data.type === 'mediaError' && fatalMediaRecoveryAttempts < maxFatalMediaRecoveries) {
          fatalMediaRecoveryAttempts += 1;
          try {
            console.warn('[HlsPlayer] fatal mediaError, próba odtworzenia', fatalMediaRecoveryAttempts, data.details, data);
            if (fatalMediaRecoveryAttempts === 2) {
              instance.swapAudioCodec?.();
            }
            instance.recoverMediaError();
            return;
          } catch (e) {
            try {
              console.warn('[HlsPlayer] recoverMediaError nieudany', e);
            } catch {}
          }
        }
        if (data.type === 'networkError') {
          try {
            instance.startLoad();
            return;
          } catch {}
        }
        try {
          console.warn('[HlsPlayer]', data.type, data.details, data.response?.code, data.url, data);
        } catch {}
        setError(formatHlsFatalError(data));
      });
      instance.on?.(hls.Events.FRAG_BUFFERED, () => {
        fatalMediaRecoveryAttempts = 0;
      });
      instance.on?.(hls.Events.MEDIA_ATTACHED, () => {
        void video.play().catch(() => {});
      });
      instance.attachMedia(video);
      // Nie ustawiamy video.currentTime do live edge — przy krótkim oknie HLS na Pi to powodowało seek na już usunięte .ts (404, bufferAppendError).
      if (live) {
        clearDrift();
        driftTimer = window.setInterval(() => {
          if (destroyed || !video) return;
          try {
            void video.play().catch(() => {});
          } catch {}
        }, 3000);
      }
      instance.loadSource(src);
      void video.play().catch(() => {});
    };

    if (hls && hls.isSupported?.()) {
      attach();
    } else {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@1.6.2/dist/hls.min.js';
      script.async = true;
      script.onload = () => {
        hls = (window as any).Hls;
        if (!destroyed) attach();
      };
      script.onerror = () => setError('Nie udało się załadować hls.js');
      document.head.appendChild(script);
      return () => {
        destroyed = true;
        clearDrift();
        if (stallHandler) {
          video.removeEventListener('stalled', stallHandler);
          video.removeEventListener('waiting', stallHandler);
          stallHandler = undefined;
        }
        try { (video as any).__hls?.destroy?.(); } catch {}
        try { document.head.removeChild(script); } catch {}
      };
    }

    return () => {
      destroyed = true;
      clearDrift();
      if (stallHandler) {
        video.removeEventListener('stalled', stallHandler);
        video.removeEventListener('waiting', stallHandler);
        stallHandler = undefined;
      }
      try { (video as any).__hls?.destroy?.(); } catch {}
    };
  }, [src, live]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }} className={className}>
      <video
        ref={videoRef}
        autoPlay={autoPlay}
        muted={muted}
        controls={controls}
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000', ...(style || {}) }}
      />
      {empty && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 12, textAlign: 'center', padding: 8 }}>
          Brak strumienia
        </div>
      )}
      {error && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: 'rgba(0,0,0,0.5)', padding: 16, textAlign: 'center' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default HlsPlayer;
